import React from "react";

import {
    OrderedListBlock,
    PagePreviewTileBlock,
    SplitBlock as SplitBlockValue,
    SplitLeftColoredPromoBlock,
    SplitLeftImagePromoBlock,
    SplitLeftRichtextShowcaseBlock,
    SplitRightArticleGridBlock,
    SplitRightSplitBlock,
} from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { delegateClickToLink } from "../../utils/delegateClickToLink";
import {
    ButtonPrimary,
    ButtonTextPrimary,
    Clickable,
    LongButton,
} from "../Clickables";
import { FeaturedRichtext } from "../FeaturedRichtext";
import { H, HLevel } from "../Headings";
import { getRatioForAspect } from "../Heros/Article";
import { RichText } from "../RichText";
import { Separator } from "../Separator";
import { Showcase } from "../Showcase";
import { SpacerBlock } from "../SpacerBlock";
import { Svg } from "../Svg";
import { ThemedModule, getThemeClass } from "../ThemedModule";
import { TileImage } from "../TileImage";

import styles from "./SplitBlock.module.scss";

const LeftColoredPromo = ({ value }: { value: SplitLeftColoredPromoBlock }) => (
    <div
        className={concatClassNames([
            styles.leftColoredPromo,
            getThemeClass(value.theme),
        ])}
    >
        {value.eyebrow && <p className={styles.eyebrow}>{value.eyebrow}</p>}
        {value.heading && <H className={styles.heading}>{value.heading}</H>}
        {value.body && <RichText className={styles.body} value={value.body} />}
        {value.cta?.url && value.cta.display_name && (
            <LongButton variant="primary" simpleLink={value.cta} />
        )}
    </div>
);

const LeftImagePromo = ({ value }: { value: SplitLeftImagePromoBlock }) => (
    <div className={styles.leftImagePromo}>
        {value.image && (
            <figure>
                <ImageChooserBlock value={value.image} />
            </figure>
        )}
        {value.cta?.url && value.cta.display_name && (
            <LongButton variant="primary" simpleLink={value.cta} />
        )}
    </div>
);

const LeftRichtextShowcase = ({
    value,
}: {
    value: SplitLeftRichtextShowcaseBlock;
}) => (
    <div className={styles.leftRichtextShowcase}>
        {value.map((block) => {
            switch (block.type) {
                case "richtext":
                    return <FeaturedRichtext key={block.id} {...block} />;
                case "showcase":
                    return <Showcase key={block.id} {...block} />;
            }
        })}
    </div>
);

const ArticleTile = ({ value }: PagePreviewTileBlock[number]) => {
    const ctaRef = React.useRef<HTMLAnchorElement>(null);
    return (
        <article
            // eslint-disable-next-line react-hooks/refs -- delegateClickToLink defers ref.current access to the returned handler
            onClick={delegateClickToLink(ctaRef)}
        >
            {"tile_type" in value ? (
                <TileImage value={value} />
            ) : (
                value.image && (
                    <ImageChooserBlock
                        attrs={
                            value.image_aspect_ratio
                                ? {
                                      style: {
                                          "--image-ar": getRatioForAspect(
                                              value.image_aspect_ratio,
                                          ),
                                      },
                                  }
                                : undefined
                        }
                        value={value.image}
                    />
                )
            )}
            {value.category && (
                <p className={styles.articleCategory}>{value.category}</p>
            )}
            <Clickable
                ref={ctaRef}
                href={value.url!}
                className={styles.articleTitle}
            >
                {value.title}
            </Clickable>
        </article>
    );
};

const RightArticleGrid = ({ value }: { value: SplitRightArticleGridBlock }) => (
    <>
        <div className={styles.rightArticleGrid}>
            {value.articles
                ?.filter((_, i) => i % 2 == 0)
                .map((props) => (
                    <ArticleTile key={props.id} {...props} />
                ))}
        </div>
        <div className={styles.rightArticleGrid}>
            {value.articles
                ?.filter((_, i) => i % 2 == 1)
                .map((props) => (
                    <ArticleTile key={props.id} {...props} />
                ))}
        </div>
    </>
);

const RightSplitBlock = ({ value }: { value: SplitRightSplitBlock }) => (
    <div className={styles.cardList}>
        {value.text && <RichText value={value.text} />}
        {value.image && <ImageChooserBlock value={value.image} />}
    </div>
);

const BeegOrderedList = ({ value }: { value: OrderedListBlock }) => (
    <ol
        className={
            value.show_numbers === false
                ? styles.rightOrderedList
                : styles.rightOrderedListWithNumbers
        }
    >
        {value.items?.map((item) => (
            <li key={item.id}>
                <p className={styles.olHeading}>{item.value.heading}</p>
                {item.value.body && <RichText value={item.value.body} />}
            </li>
        ))}
    </ol>
);

export const SplitBlock = ({ value }: { value: SplitBlockValue }) => (
    <ThemedModule theme={value.module_theme}>
        <section className={styles.root}>
            {value.left?.length ? (
                <HLevel>
                    <StreamField
                        value={value.left}
                        tagName={React.Fragment}
                        components={{
                            colored_promo: LeftColoredPromo,
                            image_promo: LeftImagePromo,
                            richtext_showcase: LeftRichtextShowcase,
                        }}
                    />
                </HLevel>
            ) : (
                <div />
            )}
            <Separator className={styles.divider} orientation="vertical" />
            <HLevel>
                <div className={styles.rightContainer}>
                    {value.right?.map((block) => {
                        let CtaComponent;
                        switch (block.type) {
                            case "article_grid":
                                return (
                                    <RightArticleGrid
                                        key={block.id}
                                        {...block}
                                    />
                                );
                            case "space":
                                return (
                                    <SpacerBlock key={block.id} {...block} />
                                );
                            case "cta":
                                CtaComponent =
                                    block.value.link_style === "button-primary"
                                        ? ButtonPrimary
                                        : ButtonTextPrimary;
                                return (
                                    <CtaComponent
                                        key={block.id}
                                        href={block.value.url ?? undefined}
                                    >
                                        {block.value.display_name}{" "}
                                        <Svg name="arrow" />
                                    </CtaComponent>
                                );
                            case "split":
                                return (
                                    <React.Fragment key={block.id}>
                                        <RightSplitBlock {...block} />
                                        <Separator orientation="horizontal" />
                                    </React.Fragment>
                                );
                            case "rich_text":
                                return (
                                    <RichText
                                        key={block.id}
                                        value={block.value}
                                    />
                                );
                            case "showcase":
                                return <Showcase key={block.id} {...block} />;
                            case "ordered_list":
                                return (
                                    <BeegOrderedList
                                        key={block.id}
                                        {...block}
                                    />
                                );
                        }
                    })}
                </div>
            </HLevel>
        </section>
    </ThemedModule>
);
